/**
 * Region selector
 * ---------------
 * Drives the country/region picker rendered by snippets/header-localization.liquid:
 *
 *  - the mobile in-menu accordion (expand/collapse, Esc, closes with the burger menu)
 *  - the country filter, which is diacritic-insensitive and matches on country
 *    name, ISO code and currency code
 *  - `return_to`, which Liquid cannot build with a query string because the
 *    `request` object has no `query_string` property. Without this, switching
 *    country from a filtered collection or a product with ?variant= drops the
 *    parameters.
 *
 * Everything degrades: with JS off the accordion panel is still reachable, the
 * lists still submit, and only the filter input stays hidden.
 */
(function () {
  'use strict';

  var selectors = {
    scope: '[data-region-selector]',
    toggle: '[data-region-toggle]',
    panel: '[data-region-panel]',
    searchWrap: '[data-region-search-wrap]',
    search: '[data-region-search]',
    list: '[data-region-list]',
    option: '[data-region-option]',
    empty: '[data-region-empty]',
    returnTo: '[data-region-return-to]',
    menuToggle: '#menu-toggle'
  };

  var classes = {
    expanded: 'is-expanded',
    enhanced: 'is-enhanced'
  };

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * Lowercase and strip combining marks so "Aland" matches "Åland" and
   * "Espana" matches "España". The explicit \u0300-\u036f range is used
   * rather than \p{Diacritic} for older Safari.
   */
  function normalize(value) {
    var text = (value == null ? '' : String(value)).toLowerCase().trim();

    if (typeof text.normalize === 'function') {
      text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    return text;
  }

  function RegionSelector(scope) {
    this.scope = scope;
    this.toggle = scope.querySelector(selectors.toggle);
    this.panel = scope.querySelector(selectors.panel);
    this.search = scope.querySelector(selectors.search);
    this.searchWrap = scope.querySelector(selectors.searchWrap);
    this.empty = scope.querySelector(selectors.empty);

    // Normalise once at init rather than on every keystroke.
    this.index = Array.prototype.map.call(
      scope.querySelectorAll(selectors.option),
      function (element) {
        return {
          element: element,
          text: normalize(element.getAttribute('data-region-haystack'))
        };
      }
    );

    this.init();
  }

  RegionSelector.prototype.init = function () {
    this.bindReturnTo();

    if (this.search) {
      this.bindSearch();
      // Only reveal the filter once it can actually do something.
      if (this.searchWrap) {
        this.searchWrap.hidden = false;
      }
    }

    if (this.toggle && this.panel) {
      // Marks the panel as safe to collapse: the CSS keeps it open until this
      // lands, so a failed script leaves the list usable rather than sealed.
      this.scope.classList.add(classes.enhanced);
      this.bindAccordion();
    }
  };

  /* --------------------------------------------------------------- return_to */

  RegionSelector.prototype.bindReturnTo = function () {
    var forms = this.scope.querySelectorAll('form');

    this.syncReturnTo();

    // Re-sync on submit too: collection filters can rewrite the URL via
    // history.pushState after this script has run.
    Array.prototype.forEach.call(
      forms,
      function (form) {
        form.addEventListener('submit', this.syncReturnTo.bind(this));
      }.bind(this)
    );
  };

  RegionSelector.prototype.syncReturnTo = function () {
    var value = window.location.pathname + window.location.search;

    Array.prototype.forEach.call(
      this.scope.querySelectorAll(selectors.returnTo),
      function (input) {
        input.value = value;
      }
    );
  };

  /* ------------------------------------------------------------------ filter */

  RegionSelector.prototype.bindSearch = function () {
    this.search.addEventListener(
      'input',
      function (event) {
        this.filter(event.target.value);
      }.bind(this)
    );

    this.search.addEventListener(
      'keydown',
      function (event) {
        // Belt and braces: the input is rendered outside <form>, but if it ever
        // moves inside one, Enter would submit the first country button.
        if (event.key === 'Enter' || event.keyCode === 13) {
          event.preventDefault();
        }
      }
    );
  };

  RegionSelector.prototype.filter = function (term) {
    var query = normalize(term);
    var visible = 0;

    this.index.forEach(function (entry) {
      var matches = !query || entry.text.indexOf(query) !== -1;
      entry.element.hidden = !matches;
      if (matches) {
        visible += 1;
      }
    });

    if (this.empty) {
      this.empty.hidden = visible !== 0;
    }
  };

  RegionSelector.prototype.resetFilter = function () {
    if (!this.search) {
      return;
    }

    this.search.value = '';
    this.filter('');
  };

  /* --------------------------------------------------------------- accordion */

  RegionSelector.prototype.bindAccordion = function () {
    this.toggle.addEventListener(
      'click',
      function () {
        this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true');
      }.bind(this)
    );

    this.scope.addEventListener(
      'keydown',
      function (event) {
        if ((event.key === 'Escape' || event.keyCode === 27) && this.isExpanded()) {
          this.setExpanded(false);
          this.toggle.focus();
        }
      }.bind(this)
    );

    // Collapse when the burger menu itself is closed, so reopening the menu
    // starts from the summary row rather than a stale open list.
    var menuToggle = document.querySelector(selectors.menuToggle);
    if (menuToggle) {
      menuToggle.addEventListener(
        'change',
        function () {
          if (!menuToggle.checked) {
            this.setExpanded(false);
          }
        }.bind(this)
      );
    }
  };

  RegionSelector.prototype.isExpanded = function () {
    return this.toggle.getAttribute('aria-expanded') === 'true';
  };

  RegionSelector.prototype.setExpanded = function (expanded) {
    this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (expanded) {
      // Deliberately does not scroll. The customer tapped a row that is already
      // in view; moving the page out from under them on expand reads as the
      // page jumping. The list grows downward and they scroll to it themselves.
      this.scope.classList.add(classes.expanded);
    } else {
      this.scope.classList.remove(classes.expanded);
      // Collapsing is the one case worth scrolling for: the row they need to
      // land back on may be far above the current position. Only chases it if
      // they actually scrolled past it.
      this.revealToggle({ onlyIfAbove: true });
      this.resetFilter();
    }

    // Deliberately no autofocus on the search input: on mobile that raises the
    // keyboard immediately and covers the list the customer wants to read.
  };

  /**
   * Scrolls the burger menu so the region row sits at the top of it. Used on
   * collapse only -- see setExpanded for why expanding stays put.
   *
   * Moves the menu only: scrollIntoView walks every scrollable ancestor and can
   * drag the page behind the menu along with it. The row sits above the panel,
   * so its target offset does not shift while the panel animates, which means
   * collapsing and scrolling can safely run together.
   */
  RegionSelector.prototype.revealToggle = function (options) {
    var scroller = this.scope.closest ? this.scope.closest('.menu') : null;

    if (!scroller) {
      return;
    }

    // Measured from the row's container, never the row itself: while expanded
    // the row is `position: sticky`, so it reports an offset of ~0 no matter
    // how far down the list the customer has scrolled, and nothing would ever
    // qualify as "scrolled past".
    var anchor = this.scope.closest('li') || this.scope;
    var offset = anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top;

    if (options && options.onlyIfAbove && offset >= 0) {
      return;
    }

    var target = scroller.scrollTop + offset;

    if (scroller.scrollTo) {
      scroller.scrollTo({ top: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } else {
      scroller.scrollTop = target;
    }
  };

  /* -------------------------------------------------------------------- init */

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(selectors.scope), function (scope) {
      if (scope.dataset.regionSelectorReady === 'true') {
        return;
      }

      scope.dataset.regionSelectorReady = 'true';
      new RegionSelector(scope);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders the header section without a page load.
  document.addEventListener('shopify:section:load', initAll);
})();
